import { PublicationService } from './publication.service';
import { BbSelectOption } from 'app/components/shared/bb-select/bb-select.component';
import { PrebidSystem, PrebidConfig, PrebidSystemType, DynamicField } from './../components/shared/bb-prebid-fields/bb-prebid-fields.component';
import { map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { LocationService } from 'app/services/location.service';
import { Injectable } from '@angular/core';

@Injectable()
export class PrebidService {
	prebidConfigJSONString: any = null;
	filteredPrebidSystems: PrebidSystem[];
	prebidSystems: PrebidSystem[];
	prebidConfigJSON: PrebidConfig; // from defaults file
	prebidParams: any; // adunit.prebidParams or publication/prebidParams
	filterParams: any;
	typeFilter: any; // optionally for filtering on type for example
	public publicationPrebidParams: any = null;

	constructor(
		private locationService: LocationService,
		private publicationService: PublicationService,
		private http: HttpClient,
	) {
		// this.getPrebidConfig().subscribe((prebidData: PrebidConfig) => this.handlePrebidData(prebidData));
		this.publicationService.getData().subscribe((publicationData: any) => this.publicationPrebidParams = publicationData?.prebidParams);
	}

	getPrebidConfig(): Observable<any> {
		const environment = this.locationService.getEnvironment();
		const env = environment && environment.replace(/\s.*$/, '');
		const cdn = 'cdn' + ((env !== 'prod') ? '-' + env : ''); // 'cdn-dev', 'cdn-acc', 'cdn-test', or 'cdn'

		if (this.prebidConfigJSONString) {
			return of(JSON.parse(this.prebidConfigJSONString));
		}

		return this.http.get('https://' + cdn + '.bluebillywig.com/scripts/prebid/prebidConfig.json').pipe(
			map((config: any) => {
				this.prebidConfigJSONString = JSON.stringify(config);
				return config;
			}
			));
	}

	/**
	 * Checks if a Prebid system is enabled on the publication level
	 *
	 * @param system - the system to be checked
	 */
	filterPrebidSystems(system: PrebidSystem) {
		if (this.typeFilter && system.type !== this.typeFilter) {
			return false;
		}
		if (this.filterParams) {
			return this.filterParams && this.filterParams[system.code] && this.filterParams[system.code].enabled;
		} else {
			return true;
		}
	}

	/*
	 * Parses the core Prebid config and prepares the data structures to be usable for the UI components
	 *
	 * @param {PrebidConfig} prebidConfigJSON - from defaults
	 */
	handlePrebidData(pbConfigJSON: PrebidConfig, filterParams = null, prebidParams = null, typeFilter = null) {
		this.typeFilter = typeFilter;
		this.filterParams = filterParams;
		this.prebidParams = prebidParams;
		if (!this.prebidParams) {
			this.prebidParams = {};
		}
		this.prebidConfigJSON = pbConfigJSON;
		this.filteredPrebidSystems = this.prebidConfigJSON.systems.filter((system: PrebidSystem) => this.filterPrebidSystems(system));

		const aliasParams = filterParams || prebidParams;
		for (const key in aliasParams) {
			if (aliasParams[key] && aliasParams[key].alias) {
				const params = aliasParams[key];
				const alias = params.alias;
				const system = this.filteredPrebidSystems.find(s => s.code === alias);

				if (system) {
					const aliasedSystem = JSON.parse(JSON.stringify(system));
					aliasedSystem.code = params.code;
					aliasedSystem.alias = params.alias;
					this.filteredPrebidSystems.push(aliasedSystem);
				}
			}
		}

		const filteredPrebidSystemExistsByCode = {};

		const configSystems: PrebidSystem[] = [];
		const analyticsSystems: PrebidSystem[] = [];
		const moduleSystems: PrebidSystem[] = [];
		const adapterSystems: PrebidSystem[] = [];

		this.filteredPrebidSystems.forEach((prebidSystem: PrebidSystem) => {
			if (prebidSystem.code) {
				filteredPrebidSystemExistsByCode[prebidSystem.code] = true;
				if (!this.prebidParams[prebidSystem.code]) {
					this.prebidParams[prebidSystem.code] = { enabled: false };
				}

				// Split out for alphabetical sorting per category...
				switch (prebidSystem.type) {
					case PrebidSystemType.Config:
						configSystems.push(prebidSystem);
						break;
					case PrebidSystemType.Analytics:
						analyticsSystems.push(prebidSystem);
						break;
					case PrebidSystemType.Module:
						moduleSystems.push(prebidSystem);
						break;
					default:
						// This is a bid adapter, add the dynamic CPM adjustment field
						// prebidSystem.dynamicFields.push(this.generateCpmAdjustmentField());
						adapterSystems.push(prebidSystem);
						break;
				}

				if (prebidSystem.dynamicFields) {
					for (const dynamicField of prebidSystem.dynamicFields) {
						dynamicField.inputType = this.getInputType(dynamicField);
						dynamicField.defaultValue = this.getDefaultValue(prebidSystem.code, dynamicField.path);

						if (this.prebidParams[prebidSystem.code] && typeof this.prebidParams[prebidSystem.code][dynamicField.path] !== 'undefined') {
							dynamicField.value = this.prebidParams[prebidSystem.code][dynamicField.path];
						}

						if (dynamicField.options) {
							dynamicField.options = this.optionsToBbOptions(dynamicField, this.prebidParams[prebidSystem.code], prebidSystem.code);
						}
					}
				}
			}
		});

		const alphabeticalSort = (a: PrebidSystem, b: PrebidSystem) => (a && b && a.label && b.label) ? a.label.localeCompare(b.label) : -1;

		this.filteredPrebidSystems =
			configSystems.sort(alphabeticalSort)
				.concat(
					analyticsSystems.sort(alphabeticalSort),
					moduleSystems.sort(alphabeticalSort),
					adapterSystems.sort(alphabeticalSort)
				);
	}

	/**
	 * Determines the input type for a given dynamic field
	 *
	 * @param - the dynamicField configuration
	 */
	getInputType(dynamicField: DynamicField) {
		const fieldType = dynamicField.type;

		switch (fieldType) {
			default:
			case 'string':
			case 'array':
				return 'text';
				break;
			case 'select':
				return 'select';
				break;
			case 'int':
				return 'number';
				break;
			case 'boolean':
				return 'checkbox';
				break;
			case 'json':
				return 'json';
				break;
		}
	}

	/**
	 * Converts options specified in prebidConfig.json to proper BbSelectOption to populate a bb-select field
	 *
	 * @param dynamicField - the dynamicField configuration
	 * @param dataBinding - the object that contains the property being configured by this bb-select
	 * @param systemCode - The system in which to search
	 *
	 */
	optionsToBbOptions(dynamicField: DynamicField, dataBinding: any, systemCode: string): BbSelectOption[] {
		if (!dynamicField.options || !dynamicField.options.length) {
			return [];
		}

		let foundDefault = false;
		const defaultValue = (typeof dataBinding[dynamicField.path] !== 'undefined')
			? dataBinding[dynamicField.path]
			: this.getDefaultValue(systemCode, dynamicField.path);

		const bbOptions = dynamicField.options.map((option: string | BbSelectOption): BbSelectOption => {
			if (!foundDefault) {
				foundDefault = (defaultValue === option);
			}
			if (typeof option === 'string') {
				return {
					label: option.toString(),
					value: option,
					default: foundDefault
				};
			} else {
				return {
					label: option.label,
					value: option.value,
					default: foundDefault
				};
			}
		});

		bbOptions.unshift({ label: 'Clear value...', value: '', default: !foundDefault });

		return bbOptions;
	}

	/**
	 * Attempts to find the closest default value from a parent entity
	 * First tries to look up a publication default, if not it tries to look up a global configuration default
	 *
	 * @param systemCode - The system in which to search
	 * @param path - The path pointing to a (nested) property
	 */
	getDefaultValue(systemCode: string, path: string) {
		if (!this.prebidConfigJSON) {
			return null;
		}

		// We first check filterParams (publication defaults),
		// if not present there we check the default specified in prebidConfig.json
		if (this.filterParams && this.filterParams[systemCode] && typeof this.filterParams[systemCode][path] !== 'undefined') {
			return this.filterParams[systemCode][path];
		}

		const system = this.prebidConfigJSON.systems && this.prebidConfigJSON.systems.filter((sys) => sys.code === systemCode)[0];

		if (system && system.config) {
			const keys = path.split(/\//);
			if (keys[0] === '') {
				keys.shift();
			}
			let val = system.config;

			for (const key of keys) {
				val = val ? val[key] : void (0);
				if (typeof val === 'undefined') {
					break;
				}
			}

			return val;
		} else {
			return void (0);
		}
	}
}
